import { Component, OnDestroy, Type, computed, inject } from '@angular/core';
import { IInvoiceListItem } from '@lb/api/mylb/interfaces';
import { IGridConfig } from '../data-grid/interfaces/igrid-config.interface';
import { DataGridComponent } from '../data-grid/data-grid.component';
import { InvoiceInstructorCellComponent } from './cells/invoice-instructor-cell/invoice-instructor-cell.component';
import { InvoiceStatusTagComponent } from './invoice-status-tag/invoice-status-tag.component';
import { InvoiceActionCellComponent } from './cells/invoice-action-cell/invoice-action-cell.component';
import { IGridCell } from '../data-grid/interfaces/igrid-cell.interface';
import { InvoiceGridService } from './invoice-grid.service';
import { PaginatorComponent } from '../paginator/paginator.component';
import { UserStateService } from '@lb/state/user-state.service';
import { InvoicePayPeriodCellComponent } from './cells/invoice-pay-period-cell/invoice-pay-period-cell.component';
import { InvoicePayPeriodFilterComponent } from './filters/invoice-pay-period-filter/invoice-pay-period-filter.component';
import { AccountStateService } from '@lb/state/account-state.service';
import { formatInvoiceDate } from './invoice-date-time.formatter';

type InvoiceGridColumnContext = {
	showInstructor: boolean;
	isAdmin: boolean;
};

type InvoiceGridColumnDef = {
	id: string;
	width: string | ((context: InvoiceGridColumnContext) => string);
	headerCell: IGridCell;
	dataCell: IGridCell;
	isVisible?: (context: InvoiceGridColumnContext) => boolean;
};

@Component({
	selector: 'lb-invoice-grid',
	standalone: true,
	templateUrl: './invoice-grid.component.html',
	styleUrls: ['./invoice-grid.component.scss'],
	imports: [DataGridComponent, PaginatorComponent]
})
export class InvoiceGridComponent implements OnDestroy {
	private readonly _gridService = inject(InvoiceGridService);
	private readonly _userSummaryState = inject(UserStateService);
	private readonly _accountStateService = inject(AccountStateService);

	pageLengthOptions = [25, 50, 100];

	invoices = this._gridService.invoices;
	invoiceCount = this._gridService.invoiceCount;
	userSummaries = this._userSummaryState.userSummaries;
	isAdmin = this._accountStateService.isAdmin;

	loading = computed(() => this._gridService.loading() || this._userSummaryState.loading());
	error = this._gridService.error;
	showInstructor = this._gridService.showInstructor;

	itemsPerPage = this._gridService.itemsPerPage;
	currentPage = this._gridService.currentPage;

	gridConfig = computed((): IGridConfig => {
		const showInstructor = this.showInstructor();
		const isAdmin = this.isAdmin();
		const context: InvoiceGridColumnContext = { showInstructor, isAdmin };

		const columns: InvoiceGridColumnDef[] = [
			{
				id: 'invoiceNumber',
				width: '100px',
				headerCell: { html: 'Invoice #' },
				dataCell: {
					html: (item: IInvoiceListItem) => `#${item.invoiceID}`
				}
			},
			{
				id: 'instructor',
				width: '1fr',
				headerCell: { html: 'Instructor' },
				dataCell: {
					html: () => '',
					component: InvoiceInstructorCellComponent as Type<Component>,
					inputs: (item: IInvoiceListItem) => {
						const summaries = this.userSummaries();

						return {
							instructorUserID: item.instructorUserID,
							instructor: summaries ? summaries[item.instructorUserID] : undefined
						};
					}
				},
				isVisible: (ctx) => ctx.showInstructor
			},
			{
				id: 'status',
				width: '150px',
				headerCell: {
					html: 'Status'
				},
				dataCell: {
					html: () => '',
					component: InvoiceStatusTagComponent as Type<Component>,
					inputs: (item: IInvoiceListItem) => ({
						invoiceStatus: item.invoiceStatus
					})
				},
				isVisible: (ctx) => ctx.isAdmin
			},
			{
				id: 'payPeriod',
				width: this.isAdmin() ? '260px' : '1fr',
				headerCell: {
					html: '',
					component: InvoicePayPeriodFilterComponent as Type<Component>,
					inputs: () => ({
						isAdmin: this.isAdmin()
					})
				},
				dataCell: {
					html: () => '',
					component: InvoicePayPeriodCellComponent as Type<Component>,
					inputs: (item: IInvoiceListItem) => ({
						payPeriod: this.formatPeriod(item.periodStartDate, item.periodEndDate),
						isCurrent: item.isCurrentPayPeriod
					})
				}
			},
			{
				id: 'totalHours',
				width: '140px',
				headerCell: { html: 'Hours' },
				dataCell: {
					html: (item: IInvoiceListItem) => `${item.totalHours.toFixed(2)}`
				},
				isVisible: (ctx) => ctx.isAdmin
			},
			{
				id: 'totalAmount',
				width: '120px',
				headerCell: { html: 'Total' },
				dataCell: {
					html: (item: IInvoiceListItem) => `$${item.totalAmount.toFixed(2)}`
				},
				isVisible: (ctx) => !ctx.isAdmin
			},
			{
				id: 'action',
				width: '75px',
				headerCell: { html: '' },
				dataCell: {
					html: () => '',
					component: InvoiceActionCellComponent as Type<Component>,
					inputs: (item: IInvoiceListItem) => ({
						invoiceID: item.invoiceID,
						isAdmin: this.isAdmin()
					})
				}
			}
		];

		const visibleColumns = columns.filter((column) => (column.isVisible ? column.isVisible(context) : true));
		const columnConfig = visibleColumns.map((column) => (typeof column.width === 'function' ? column.width(context) : column.width)).join(' ');
		const headerCells: IGridCell[] = visibleColumns.map((column) => column.headerCell);
		const dataCells: IGridCell[] = visibleColumns.map((column) => column.dataCell);

		return {
			gridName: 'Invoices',
			cellBorders: false,
			noItemsMessage: 'No invoices found...',
			columnConfig,
			headerConfig: {
				trackRowsBy: (index: number) => `${index}`,
				trackCellsBy: (index: number) => `${index}`,
				rows: [
					{
						cells: headerCells
					}
				]
			},
			dataConfig: {
				trackRowsBy: (index: number) => `${index}`,
				trackCellsBy: (index: number) => `${index}`,
				rowDef: {
					cells: dataCells
				}
			}
		};
	});

	ngOnDestroy(): void {
		this._gridService.clearState();
	}

	public onPageChange(page: number): void {
		this._gridService.updateFilters({ page: page });
	}

	public onPageSizeChange(pageSize: number): void {
		this._gridService.updateFilters({ pageSize: pageSize });
	}

	private formatDate(dateStr?: string): string {
		return formatInvoiceDate(dateStr);
	}

	private formatPeriod(start?: string, end?: string): string {
		const s = start ? this.formatDate(start) : '—';
		const e = end ? this.formatDate(end) : '—';
		return `${s} → ${e}`;
	}
}
